/**
 * Campaign tree renderer — SVG edges + DOM nodes, pan/zoom via CSS transform.
 * Desktop: wheel zoom + drag pan. Mobile: pinch zoom + one/two-finger pan.
 * Admin: drag move + corner resize (circular).
 */

import type { CampaignNodeState, CampaignNodeView } from "./types";
import {
  CAMPAIGN_NODE_DIAMETER_MAX,
  CAMPAIGN_NODE_DIAMETER_MIN,
  campaignNodeDiameterPx,
} from "./types";

export type CampaignTreeMountOptions = {
  root: HTMLElement;
  nodes: CampaignNodeView[];
  /** Player: click available/completed. Admin: click any. */
  onNodeActivate?: (node: CampaignNodeView, el: HTMLElement) => void;
  /** Admin drag end. */
  onNodeMoved?: (nodeId: string, posX: number, posY: number) => void;
  /** Admin corner-resize end (diameter always circular). */
  onNodeResized?: (nodeId: string, nodeDiameter: number) => void;
  interactive?: boolean;
  editable?: boolean;
  selectedId?: string | null;
  /** Center the viewport on this node after mount (falls back to fitView). */
  focusNodeId?: string | null;
};

const WORLD_PAD = 120;
const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;

function stateClass(state: CampaignNodeState | undefined): string {
  if (state === "completed") return "campaignNode--completed";
  if (state === "available") return "campaignNode--available";
  return "campaignNode--locked";
}

function clampScale(s: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
}

function clampDiameter(d: number) {
  return Math.min(
    CAMPAIGN_NODE_DIAMETER_MAX,
    Math.max(CAMPAIGN_NODE_DIAMETER_MIN, Math.round(d)),
  );
}

function stateFill(n: CampaignNodeView): string | null {
  const colors = n.stateColors;
  if (!colors) return null;
  if (n.state === "completed") return colors.completed || null;
  if (n.state === "available") return colors.available || null;
  return colors.locked || null;
}

export type CampaignTreeApi = {
  setNodes: (nodes: CampaignNodeView[]) => void;
  setSelectedId: (id: string | null) => void;
  destroy: () => void;
  fitView: () => void;
  centerOnNode: (nodeId: string, zoom?: number) => boolean;
};

export function mountCampaignTree(opts: CampaignTreeMountOptions): CampaignTreeApi {
  const { root } = opts;
  root.replaceChildren();
  root.classList.add("campaignTreeRoot");

  const viewport = document.createElement("div");
  viewport.className = "campaignTreeViewport";
  const world = document.createElement("div");
  world.className = "campaignTreeWorld";
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.classList.add("campaignTreeEdges");
  svg.setAttribute("aria-hidden", "true");
  const nodesLayer = document.createElement("div");
  nodesLayer.className = "campaignTreeNodes";
  world.append(svg, nodesLayer);
  viewport.appendChild(world);
  root.appendChild(viewport);

  let nodes = opts.nodes.slice();
  let selectedId = opts.selectedId ?? null;
  let scale = 1;
  let panX = 0;
  let panY = 0;
  let draggingId: string | null = null;
  let resizingId: string | null = null;
  let dragOffsetX = 0;
  let dragOffsetY = 0;
  let suppressClick = false;

  /** Active pointers for pan / pinch (no capture so two fingers both track). */
  const pointers = new Map<number, { x: number; y: number }>();
  let panPointerId: number | null = null;
  let panStartX = 0;
  let panStartY = 0;
  let panOriginX = 0;
  let panOriginY = 0;
  let pinchActive = false;
  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let pinchStartMidX = 0;
  let pinchStartMidY = 0;
  let pinchStartPanX = 0;
  let pinchStartPanY = 0;

  function applyTransform() {
    world.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
  }

  function worldBounds() {
    if (!nodes.length) return { minX: 0, minY: 0, maxX: 400, maxY: 300 };
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of nodes) {
      const r = campaignNodeDiameterPx(n) / 2;
      minX = Math.min(minX, n.posX - r);
      minY = Math.min(minY, n.posY - r);
      maxX = Math.max(maxX, n.posX + r);
      maxY = Math.max(maxY, n.posY + r);
    }
    return {
      minX: minX - WORLD_PAD,
      minY: minY - WORLD_PAD,
      maxX: maxX + WORLD_PAD,
      maxY: maxY + WORLD_PAD,
    };
  }

  function fitView() {
    const b = worldBounds();
    const w = Math.max(1, b.maxX - b.minX);
    const h = Math.max(1, b.maxY - b.minY);
    const vw = Math.max(1, viewport.clientWidth);
    const vh = Math.max(1, viewport.clientHeight);
    scale = Math.min(1.4, Math.max(0.35, Math.min(vw / w, vh / h) * 0.92));
    panX = (vw - w * scale) / 2 - b.minX * scale;
    panY = (vh - h * scale) / 2 - b.minY * scale;
    applyTransform();
  }

  /** Pan so `nodeId` sits in the viewport center at a readable zoom. */
  function centerOnNode(nodeId: string, zoom = 1): boolean {
    const n = nodes.find((x) => x.id === nodeId);
    if (!n) return false;
    const vw = Math.max(1, viewport.clientWidth);
    const vh = Math.max(1, viewport.clientHeight);
    if (vw < 16 || vh < 16) return false;
    scale = clampScale(zoom);
    panX = vw / 2 - n.posX * scale;
    panY = vh / 2 - n.posY * scale;
    applyTransform();
    return true;
  }

  function initialView() {
    if (opts.focusNodeId && centerOnNode(opts.focusNodeId, 1)) return;
    fitView();
  }

  function clientToWorld(clientX: number, clientY: number) {
    const rect = viewport.getBoundingClientRect();
    return {
      x: (clientX - rect.left - panX) / scale,
      y: (clientY - rect.top - panY) / scale,
    };
  }

  function viewportPoint(clientX: number, clientY: number) {
    const rect = viewport.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function pointerList() {
    return Array.from(pointers.values());
  }

  function twoFingerMetrics() {
    const pts = pointerList();
    if (pts.length < 2) return null;
    const a = pts[0];
    const b = pts[1];
    return {
      dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
      midX: (a.x + b.x) / 2,
      midY: (a.y + b.y) / 2,
    };
  }

  function beginPinch() {
    const m = twoFingerMetrics();
    if (!m) return;
    pinchActive = true;
    panPointerId = null;
    draggingId = null;
    resizingId = null;
    pinchStartDist = m.dist;
    pinchStartScale = scale;
    const mid = viewportPoint(m.midX, m.midY);
    pinchStartMidX = mid.x;
    pinchStartMidY = mid.y;
    pinchStartPanX = panX;
    pinchStartPanY = panY;
    suppressClick = true;
  }

  function updatePinch() {
    const m = twoFingerMetrics();
    if (!m || !pinchActive) return;
    const mid = viewportPoint(m.midX, m.midY);
    const newScale = clampScale(pinchStartScale * (m.dist / pinchStartDist));
    const worldX = (pinchStartMidX - pinchStartPanX) / pinchStartScale;
    const worldY = (pinchStartMidY - pinchStartPanY) / pinchStartScale;
    scale = newScale;
    panX = mid.x - worldX * scale;
    panY = mid.y - worldY * scale;
    applyTransform();
  }

  function applyNodeVisual(el: HTMLElement, n: CampaignNodeView) {
    const d = campaignNodeDiameterPx(n);
    el.style.setProperty("--campaign-node-d", `${d}px`);
    const fill = stateFill(n);
    if (fill) {
      el.classList.add("campaignNode--customColor");
      el.style.setProperty("--campaign-node-fill", fill);
      el.style.setProperty("--campaign-node-border", fill);
    } else {
      el.classList.remove("campaignNode--customColor");
      el.style.removeProperty("--campaign-node-fill");
      el.style.removeProperty("--campaign-node-border");
    }
  }

  function renderEdges() {
    svg.replaceChildren();
    const b = worldBounds();
    const w = Math.max(1, b.maxX - b.minX);
    const h = Math.max(1, b.maxY - b.minY);
    svg.setAttribute("width", String(w));
    svg.setAttribute("height", String(h));
    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    svg.style.left = `${b.minX}px`;
    svg.style.top = `${b.minY}px`;
    const byId = new Map(nodes.map((n) => [n.id, n]));
    for (const n of nodes) {
      for (const pid of n.parentIds) {
        const p = byId.get(pid);
        if (!p) continue;
        const x1 = p.posX - b.minX;
        const y1 = p.posY - b.minY;
        const x2 = n.posX - b.minX;
        const y2 = n.posY - b.minY;
        const midY = (y1 + y2) / 2;
        const parentCleared = opts.editable || p.state === "completed";
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute(
          "d",
          `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`,
        );
        path.setAttribute(
          "class",
          `campaignTreeEdge ${parentCleared ? "campaignTreeEdge--cleared" : "campaignTreeEdge--pending"}`,
        );
        path.setAttribute("fill", "none");
        path.setAttribute("stroke", "currentColor");
        path.setAttribute("stroke-width", parentCleared ? "2.8" : "2.2");
        path.setAttribute("stroke-linecap", "round");
        if (!parentCleared) path.setAttribute("stroke-dasharray", "7 6");
        svg.appendChild(path);
      }
    }
  }

  function renderNodes() {
    nodesLayer.replaceChildren();
    for (const n of nodes) {
      const el = document.createElement("button");
      el.type = "button";
      el.className = `campaignNode ${stateClass(n.state)}${n.isFinal ? " campaignNode--final" : ""}${
        selectedId === n.id ? " campaignNode--selected" : ""
      }`;
      el.style.left = `${n.posX}px`;
      el.style.top = `${n.posY}px`;
      el.dataset.nodeId = n.id;
      el.setAttribute("aria-label", n.label);
      applyNodeVisual(el, n);
      if (n.state === "locked" && !opts.editable) {
        el.disabled = true;
      }

      const circle = document.createElement("span");
      circle.className = "campaignNodeCircle";
      if (n.state === "completed") {
        const check = document.createElement("span");
        check.className = "campaignNodeCheck";
        check.textContent = "✓";
        circle.appendChild(check);
      }
      const label = document.createElement("span");
      label.className = "campaignNodeLabel";
      label.textContent = n.label;
      el.append(circle, label);

      if (opts.editable) {
        const handle = document.createElement("span");
        handle.className = "campaignNodeResize";
        handle.title = "Drag to resize";
        handle.addEventListener("pointerdown", (e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          e.preventDefault();
          resizingId = n.id;
          draggingId = null;
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          suppressClick = true;
          try {
            handle.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
        });
        el.appendChild(handle);
      }

      if (opts.interactive !== false) {
        el.addEventListener("pointerdown", (e) => {
          if (pointers.size >= 1) return;
          if ((e.target as HTMLElement).closest(".campaignNodeResize")) return;
          if (opts.editable && e.button === 0) {
            draggingId = n.id;
            resizingId = null;
            const wpt = clientToWorld(e.clientX, e.clientY);
            dragOffsetX = n.posX - wpt.x;
            dragOffsetY = n.posY - wpt.y;
            pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            e.stopPropagation();
            return;
          }
        });
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          if (draggingId || resizingId || suppressClick || pinchActive || pointers.size > 1) return;
          opts.onNodeActivate?.(n, el);
        });
      }
      nodesLayer.appendChild(el);
    }
  }

  function render() {
    renderEdges();
    renderNodes();
  }

  viewport.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const beforeX = (mx - panX) / scale;
      const beforeY = (my - panY) / scale;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      scale = clampScale(scale * delta);
      panX = mx - beforeX * scale;
      panY = my - beforeY * scale;
      applyTransform();
    },
    { passive: false },
  );

  viewport.addEventListener(
    "pointerdown",
    (e) => {
      if (e.button !== 0 && e.pointerType === "mouse") return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size >= 2) {
        beginPinch();
        e.preventDefault();
        return;
      }

      if ((e.target as HTMLElement).closest(".campaignNode")) {
        return;
      }

      panPointerId = e.pointerId;
      panStartX = e.clientX;
      panStartY = e.clientY;
      panOriginX = panX;
      panOriginY = panY;
    },
    { passive: false },
  );

  viewport.addEventListener(
    "pointermove",
    (e) => {
      if (!pointers.has(e.pointerId) && !draggingId && !resizingId) return;
      if (pointers.has(e.pointerId)) {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      }

      if (pointers.size >= 2) {
        if (!pinchActive) beginPinch();
        updatePinch();
        e.preventDefault();
        return;
      }

      if (resizingId && opts.editable) {
        const node = nodes.find((n) => n.id === resizingId);
        if (node) {
          const wpt = clientToWorld(e.clientX, e.clientY);
          const dist = Math.hypot(wpt.x - node.posX, wpt.y - node.posY);
          // Corner is on the SE rim — diameter ≈ √2 * radius * 2 from center-to-corner,
          // but measuring center→pointer as radius keeps the circle intuitive.
          node.nodeDiameter = clampDiameter(dist * 2);
          render();
        }
        e.preventDefault();
        return;
      }

      if (draggingId && opts.editable) {
        const wpt = clientToWorld(e.clientX, e.clientY);
        const node = nodes.find((n) => n.id === draggingId);
        if (node) {
          node.posX = Math.round(wpt.x + dragOffsetX);
          node.posY = Math.round(wpt.y + dragOffsetY);
          render();
        }
        return;
      }

      if (panPointerId === e.pointerId) {
        panX = panOriginX + (e.clientX - panStartX);
        panY = panOriginY + (e.clientY - panStartY);
        if (Math.hypot(e.clientX - panStartX, e.clientY - panStartY) > 8) {
          suppressClick = true;
        }
        applyTransform();
      }
    },
    { passive: false },
  );

  const endPointer = (e: PointerEvent) => {
    pointers.delete(e.pointerId);

    if (resizingId && opts.editable && pointers.size === 0) {
      const node = nodes.find((n) => n.id === resizingId);
      const id = resizingId;
      resizingId = null;
      if (node) {
        const d = clampDiameter(campaignNodeDiameterPx(node));
        node.nodeDiameter = d;
        opts.onNodeResized?.(id, d);
      }
      window.setTimeout(() => {
        suppressClick = false;
      }, 40);
    }

    if (draggingId && opts.editable && pointers.size === 0) {
      const node = nodes.find((n) => n.id === draggingId);
      const id = draggingId;
      draggingId = null;
      if (node) opts.onNodeMoved?.(id, node.posX, node.posY);
    }

    if (pointers.size >= 2) {
      beginPinch();
      return;
    }

    if (pinchActive) {
      pinchActive = false;
      if (pointers.size === 1) {
        const [pid, pt] = Array.from(pointers.entries())[0];
        panPointerId = pid;
        panStartX = pt.x;
        panStartY = pt.y;
        panOriginX = panX;
        panOriginY = panY;
      } else {
        panPointerId = null;
      }
      window.setTimeout(() => {
        suppressClick = false;
      }, 280);
      return;
    }

    if (panPointerId === e.pointerId) {
      panPointerId = null;
      window.setTimeout(() => {
        suppressClick = false;
      }, 40);
    }
  };
  viewport.addEventListener("pointerup", endPointer);
  viewport.addEventListener("pointercancel", endPointer);
  viewport.addEventListener("pointerleave", (e) => {
    if (pointers.has(e.pointerId)) endPointer(e);
  });

  render();
  requestAnimationFrame(initialView);

  return {
    setNodes(next) {
      nodes = next.slice();
      render();
    },
    setSelectedId(id) {
      selectedId = id;
      render();
    },
    destroy() {
      root.replaceChildren();
      root.classList.remove("campaignTreeRoot");
    },
    fitView,
    centerOnNode,
  };
}
